import { ApolloClient, InMemoryCache, createHttpLink, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { fetchAuthSession } from "aws-amplify/auth";
import { getSelectedCustomerId } from "../../hooks/useCustomer";
import { AuthenticationService } from "../authentication";

const getGraphQLUri = () => {
  if (import.meta.env.VITE_VIEWTRANSFORMS_URL) {
    return `${import.meta.env.VITE_VIEWTRANSFORMS_URL}/graphql`;
  }

  const host = import.meta.env.VITE_HTTP_API_V2_HOST;
  if (!host) {
    throw new Error(
      "Either VITE_VIEWTRANSFORMS_URL or VITE_HTTP_API_V2_HOST must be set",
    );
  }

  return `https://${host}/viewtransforms/graphql`;
};

const httpLink = createHttpLink({
  uri: getGraphQLUri(),
  fetchOptions: { priority: "high" } as RequestInit,
});

const authLink = setContext(async (_, { headers }) => {
  let accessToken = AuthenticationService.getAccessToken();
  let idToken = AuthenticationService.getIdToken();

  if (!accessToken || !idToken) {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        const tokenString = session.tokens.accessToken.toString();
        accessToken = tokenString;
        if (!AuthenticationService.getAccessToken()) {
          AuthenticationService.setAccessToken(tokenString);
        }
      }
      if (session.tokens?.idToken) {
        const tokenString = session.tokens.idToken.toString();
        idToken = tokenString;
        if (!AuthenticationService.getIdToken()) {
          AuthenticationService.setIdToken(tokenString);
        }
      }
    } catch (error) {
      console.error("[VT Apollo] Failed to fetch auth session:", error);
    }
  }

  if (!accessToken) {
    return { headers };
  }

  const accessTokenValue = accessToken.startsWith("Bearer ")
    ? accessToken.substring(7)
    : accessToken;

  const headersToReturn: Record<string, string> = {
    ...headers,
    "Content-Type": "application/json",
    authorization: `Bearer ${accessTokenValue}`,
  };

  if (idToken) {
    const idTokenValue = idToken.startsWith("Bearer ") ? idToken.substring(7) : idToken;
    headersToReturn["x-atomic-idtoken"] = idTokenValue;

    try {
      const payload = JSON.parse(atob(idTokenValue.split(".")[1]));
      if (payload["custom:role"] === "ROOT") {
        headersToReturn["x-atomic-customerid"] = getSelectedCustomerId();
      }
    } catch {
      // token decode failure is non-fatal here
    }
  }

  return { headers: headersToReturn };
});

export const viewTransformsClient = new ApolloClient({
  link: from([authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { errorPolicy: "all" },
    query: { errorPolicy: "all" },
  },
});
